import { useMemo, useState } from 'react';
import apiClient from '../../../Services/ApiClient';
import { useAppContext } from '../../../Services/AppContext';
import {
	useGetUserOriginalIdQuery,
	useLazyMedicalHistoryTypeQuery,
} from '../../../graphql/graphql';
import { useEffectAsync } from '../../../helpers';
import { ItemScheme } from '../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../Enums/MagicField.enum';
import { PreviewFile } from '../../../Components/MediaViewer/MediaViewer';
import useTranslation from '../../../Hooks/useTranslation';
import useApiClient from '../../Base/Hooks/useApiClient';
import {
	MEDICAL_HISTORY_TYPES_TAKE,
	MedicalHistoryItem,
	buildMedicalHistoryItems,
	createMedicalHistoryDocument,
	toMedicalHistoryChild,
} from '../Services/medicalHistory.service';
import useLoadMedicalHistoryDocument from './useLoadMedicalHistoryDocument';

export type { MedicalHistoryItem };

export default function usePatientMedicalHistory(patientId: string) {
	const { socketUrl } = useAppContext();
	const { displayError } = useApiClient();
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const { workplaceId, userData } = useAppContext();
	const [getMedicalHistoryType] = useLazyMedicalHistoryTypeQuery();
	const [medicalHistory, setMedicalHistory] = useState<MedicalHistoryItem[]>(
		[],
	);
	const [medicalHistoryDocumentId, setMedicalHistoryDocumentId] = useState<
		string | undefined
	>(undefined);
	const [medicalHistoryFormScheme, setMedicalHistoryFormScheme] = useState<
		ItemScheme[]
	>([]);
	const loadMedicalHistoryDocument = useLoadMedicalHistoryDocument();
	const user = useGetUserOriginalIdQuery({ id: patientId });

	const originalId = useMemo(() => {
		return user?.data?.user?.originalId ?? undefined;
	}, [user]);

	const fetchData = async () => {
		setIsLoading(true);
		const data = await getPatientMedicalHistory();
		if (data) {
			const sortedData = [...data].sort((a, b) => a.order - b.order);
			setMedicalHistory(sortedData);
			generateFormScheme(sortedData);
			setIsLoading(false);
		}
	};

	useEffectAsync(async () => {
		await fetchData();
	}, []);

	const loadMedicalHistoryTypes = async () => {
		const { data } = await getMedicalHistoryType({
			where: { workplaceId, active: true },
			pagination: {
				take: MEDICAL_HISTORY_TYPES_TAKE,
			},
		});

		return data?.medicalHistoryType?.data ?? [];
	};

	const getPatientMedicalHistory = async (): Promise<
		MedicalHistoryItem[] | undefined
	> => {
		setIsLoading(true);
		if (!originalId) {
			return;
		}
		const document = await loadMedicalHistoryDocument(originalId);

		const types = await loadMedicalHistoryTypes();

		//IF PATIENT HAS DOCUMENT CREATED
		if (document) {
			setMedicalHistoryDocumentId(document.id);

			return buildMedicalHistoryItems(types, document.items);
		}

		//INIT NEW DOCUMENT
		const newDocumentId = await getNewDocumentId();
		if (newDocumentId) {
			setMedicalHistoryDocumentId(newDocumentId);
		}

		return buildMedicalHistoryItems(types);
	};

	const generateFormScheme = (data: MedicalHistoryItem[]) => {
		const temp: ItemScheme[] = [];
		data.forEach(item => {
			temp.push({
				type: MagicField.TEXTAREA,
				key: item.key,
				title: item.title,
				defaultValue: item.value,
			});
		});
		setMedicalHistoryFormScheme(temp);
	};

	const getNewDocumentId = async (): Promise<string | undefined> => {
		setIsLoading(true);
		try {
			return await createMedicalHistoryDocument({
				workplaceId,
				patientOriginalId: originalId as string,
				createdById: userData.id,
			});
		} catch (e) {
			displayError(e);
		}
		return undefined;
	};

	const updateMedicalHistory = async (values: Record<string, string>) => {
		setIsLoading(true);
		const data: { children: object[] } = {
			children: [],
		};

		for (const key in values) {
			if (Object.prototype.hasOwnProperty.call(values, key)) {
				const medicalHistoryItem = medicalHistory.find(
					item => item.key === key,
				);
				if (medicalHistoryItem) {
					data.children.push(
						toMedicalHistoryChild(medicalHistoryItem, values[key]),
					);
				}
			}
		}

		try {
			return await apiClient.post(
				`/document/${medicalHistoryDocumentId}/copy`,
				data,
			);
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
		return;
	};

	const documentFile: PreviewFile | undefined = useMemo(() => {
		if (medicalHistoryDocumentId) {
			return {
				id: medicalHistoryDocumentId,
				displayName: t('component.patientMedicalHistory.label') || '',
				type: 'pdf',
				preview:
					socketUrl + '/document/' + medicalHistoryDocumentId + '/pdf/download',
				download:
					socketUrl + '/document/' + medicalHistoryDocumentId + '/pdf/download',
			};
		}
		return undefined;
	}, [medicalHistoryDocumentId]);

	const getDocumentationFileWithDate = (
		date: string,
	): PreviewFile | undefined => {
		if (medicalHistoryDocumentId) {
			return {
				id: medicalHistoryDocumentId,
				displayName: t('component.patientMedicalHistory.label') || '',
				type: 'pdf',
				preview: `${socketUrl}/document/${medicalHistoryDocumentId}/pdf/download?date=${date}`,
				download: `${socketUrl}/document/${medicalHistoryDocumentId}/pdf/download?date=${date}`,
			};
		}
		return undefined;
	};

	return {
		medicalHistory,
		medicalHistoryFormScheme,
		updateMedicalHistory,
		reFetch: fetchData,
		documentFile,
		isLoading,
		medicalHistoryDocumentId,
		getDocumentationFileWithDate,
	};
}
